import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Trash2, Plus, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { PayrollAdjustmentItem } from '@/hooks/useTeacherPayroll';
import { useUpdatePayrollAdjustments } from '@/hooks/usePayrollAdjustments';
import { useToast } from '@/hooks/use-toast';

interface PayrollAdjustmentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportId: string;
  teacherName: string;
  month: number;
  year: number;
  initialAdjustments: PayrollAdjustmentItem[];
}

export const PayrollAdjustmentsDialog = ({
  open,
  onOpenChange,
  reportId,
  teacherName,
  month,
  year,
  initialAdjustments,
}: PayrollAdjustmentsDialogProps) => {
  const [items, setItems] = useState<PayrollAdjustmentItem[]>(initialAdjustments);
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'income' | 'deduction'>('deduction');
  const [isTaxable, setIsTaxable] = useState(true);

  const updateAdjustments = useUpdatePayrollAdjustments();
  const { toast } = useToast();

  const saveItems = async (newItems: PayrollAdjustmentItem[]) => {
    await updateAdjustments.mutateAsync({ reportId, adjustments: newItems });
  };

  const handleAdd = async () => {
    const parsedAmount = parseFloat(amount);
    if (!label.trim() || !parsedAmount || parsedAmount <= 0) return;

    const newItem: PayrollAdjustmentItem = { label: label.trim(), amount: parsedAmount, type, is_taxable: isTaxable };
    const newItems = [...items, newItem];

    setItems(newItems);
    setLabel('');
    setAmount('');
    setType('deduction');
    setIsTaxable(true);

    try {
      await saveItems(newItems);
      toast({ title: 'เพิ่มรายการสำเร็จ' });
    } catch {
      setItems(items);
    }
  };

  const handleRemove = async (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);

    try {
      await saveItems(newItems);
      toast({ title: 'ลบรายการสำเร็จ' });
    } catch {
      setItems(items);
    }
  };

  const totalIncome = items.filter((a) => a.type === 'income').reduce((s, a) => s + a.amount, 0);
  const totalDeduction = items.filter((a) => a.type === 'deduction').reduce((s, a) => s + a.amount, 0);

  const fmt = (n: number) =>
    n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>รายการเพิ่ม / หัก</DialogTitle>
          <DialogDescription>
            {teacherName} — เดือน {month}/{year}
          </DialogDescription>
        </DialogHeader>

        {/* Existing items */}
        <div className="space-y-2 max-h-60 overflow-y-auto py-1">
          {items.length > 0 ? (
            items.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm"
              >
                <div className="flex items-center gap-2 min-w-0">
                  {item.type === 'income' ? (
                    <ArrowUpCircle className="h-4 w-4 text-green-500 shrink-0" />
                  ) : (
                    <ArrowDownCircle className="h-4 w-4 text-destructive shrink-0" />
                  )}
                  <span className="truncate">{item.label}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {item.is_taxable && (
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      หักภาษี 3%
                    </Badge>
                  )}
                  <Badge
                    variant={item.type === 'income' ? 'default' : 'destructive'}
                    className="font-mono text-xs"
                  >
                    {item.type === 'income' ? '+' : '-'}{fmt(item.amount)}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemove(index)}
                    disabled={updateAdjustments.isPending}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-sm text-muted-foreground py-4">ยังไม่มีรายการ</p>
          )}
        </div>

        {/* Summary */}
        {items.length > 0 && (
          <div className="rounded-md bg-muted/50 px-3 py-2 text-sm space-y-1">
            {totalIncome > 0 && (
              <div className="flex justify-between text-green-600">
                <span>รวมเพิ่ม</span>
                <span className="font-mono">+{fmt(totalIncome)}</span>
              </div>
            )}
            {totalDeduction > 0 && (
              <div className="flex justify-between text-destructive">
                <span>รวมหัก</span>
                <span className="font-mono">-{fmt(totalDeduction)}</span>
              </div>
            )}
            <div className="flex justify-between font-medium border-t pt-1 mt-1">
              <span>ผลรวม</span>
              <span className={`font-mono ${totalIncome - totalDeduction >= 0 ? 'text-green-600' : 'text-destructive'}`}>
                {totalIncome - totalDeduction >= 0 ? '+' : ''}{fmt(totalIncome - totalDeduction)}
              </span>
            </div>
          </div>
        )}

        {/* Add form */}
        <div className="border-t pt-4 space-y-3">
          <p className="text-sm font-medium">เพิ่มรายการใหม่</p>
          <div className="space-y-2">
            <Label htmlFor="adj-label">ชื่อรายการ</Label>
            <Input
              id="adj-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="เช่น ค่าอุปกรณ์, โบนัสพิเศษ"
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
            />
          </div>
          <div className="flex gap-2">
            <div className="flex-1 space-y-2">
              <Label htmlFor="adj-amount">จำนวนเงิน (บาท)</Label>
              <Input
                id="adj-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              />
            </div>
            <div className="w-32 space-y-2">
              <Label>ประเภท</Label>
              <Select value={type} onValueChange={(v) => setType(v as 'income' | 'deduction')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="income">
                    <span className="text-green-600">เพิ่ม</span>
                  </SelectItem>
                  <SelectItem value="deduction">
                    <span className="text-destructive">หัก</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-md border px-3 py-2">
            <Label htmlFor="adj-taxable" className="text-sm cursor-pointer">
              หักภาษี 3% ด้วย
            </Label>
            <Switch
              id="adj-taxable"
              checked={isTaxable}
              onCheckedChange={setIsTaxable}
            />
          </div>
          <Button
            className="w-full"
            onClick={handleAdd}
            disabled={!label.trim() || !amount || parseFloat(amount) <= 0 || updateAdjustments.isPending}
          >
            {updateAdjustments.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            เพิ่มรายการ
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
